import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, LineChart, Shield, MessageSquare, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const LandingPage = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Chat Natural",
      description: "Registra comidas escribiendo como hablarías. Sin formularios complicados."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Cálculo Instantáneo",
      description: "IA que detecta alimentos y calcula calorías y macros automáticamente."
    },
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Seguimiento Visual",
      description: "Gráficos claros de tu progreso diario y semanal."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Base Española",
      description: "Alimentos adaptados a la dieta mediterránea y europea."
    }
  ];

  const premiumFeatures = [
    "Ajuste automático según actividad física",
    "Análisis semanal inteligente",
    "Predicción de cambio de peso",
    "Exportación de datos en PDF",
    "Modo Coach Directo",
    "Gráficos avanzados de evolución"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-semibold text-lg">NutriChat AI</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/chat">
                <Button className="rounded-full" data-testid="nav-chat-btn">
                  Ir al Chat
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full" data-testid="nav-login-btn">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full" data-testid="nav-register-btn">
                    Empezar Gratis
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium text-secondary-foreground mb-6">
                <Sparkles className="w-4 h-4" />
                Nutrición inteligente con IA
              </span>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
                Tu nutricionista
                <br />
                <span className="text-primary">en el bolsillo</span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Registra comidas en lenguaje natural. La IA calcula calorías y macros al instante. 
                Simple, preciso y diseñado para tu día a día.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="rounded-full px-8 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-start-btn">
                    Empezar Gratis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="secondary" className="rounded-full px-8" data-testid="hero-login-btn">
                    Ya tengo cuenta
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-card rounded-3xl shadow-card p-6 border border-border/50">
                <div className="space-y-4">
                  {/* Mock chat messages */}
                  <div className="flex justify-end">
                    <div className="chat-bubble-user px-4 py-3 max-w-[80%]">
                      <p className="text-sm">He desayunado tostadas con aceite y un café con leche</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-start">
                    <div className="chat-bubble-ai px-4 py-3 max-w-[85%]">
                      <p className="text-sm font-medium mb-2">Registrado tu desayuno:</p>
                      <div className="space-y-1 text-sm">
                        <p>• Tostadas con aceite: 190 kcal</p>
                        <p>• Café con leche: 25 kcal</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <p className="font-mono text-lg font-semibold text-primary">215 kcal total</p>
                        <p className="text-xs text-muted-foreground">Prot: 5g | Carbs: 28g | Grasa: 9g</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Mock input */}
                <div className="mt-4 flex items-center gap-3 p-3 bg-muted rounded-xl">
                  <input 
                    type="text" 
                    placeholder="Escribe lo que has comido..."
                    className="flex-1 bg-transparent text-sm outline-none"
                    disabled
                  />
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-primary-foreground" />
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-accent text-accent-foreground px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                1,785 / 2,000 kcal
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
              Nutrición sin complicaciones
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Diseñado para que registrar lo que comes sea tan fácil como enviar un mensaje
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-6 border border-border/50 hover-lift"
              >
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mb-4 text-primary">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Premium
              </span>
              
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-6">
                Lleva tu nutrición
                <br />al siguiente nivel
              </h2>
              
              <p className="text-muted-foreground mb-8">
                Desbloquea funciones avanzadas para un control total de tu alimentación 
                y alcanzar tus objetivos más rápido.
              </p>
              
              <ul className="space-y-3 mb-8">
                {premiumFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
                      <ArrowRight className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-heading text-4xl font-semibold">9,99€</span>
                <span className="text-muted-foreground">/mes</span>
              </div>
              
              <Link to="/register">
                <Button className="rounded-full px-8" data-testid="premium-cta-btn">
                  Empezar Prueba Gratis
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1670272505340-d906d8d77d03?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Usuario usando la app"
                className="rounded-3xl shadow-card"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-primary-foreground mb-6">
              Empieza a controlar tu alimentación hoy
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
              Únete a miles de usuarios que ya usan NutriChat AI para llevar una vida más saludable.
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                variant="secondary" 
                className="rounded-full px-8 shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-register-btn"
              >
                Crear Cuenta Gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-medium">NutriChat AI</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 NutriChat AI. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
